import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ROUTES } from "../Routes";
import { Language, STORAGE_KEYS } from "../constants";
import { useStrings } from "../i18n";

type Consent = "granted" | "denied";

declare global {
  interface Window {
    _startAnalytics?: () => void;
    _stopAnalytics?: () => void;
  }
}

// Also parsed by public/ar/viewer.html
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;
export const CONSENT_RESET_EVENT = "cookie-consent-reset";

function readConsent(): Consent | null {
  try {
    const { value, at } = JSON.parse(localStorage.getItem(STORAGE_KEYS.analyticsConsent) || "null");
    if (!(Date.now() - at < CONSENT_MAX_AGE_MS)) return null;
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

function clearAnalyticsCookies() {
  const host = window.location.hostname;
  const domains = ["", `; domain=${host}`, `; domain=.${host.split(".").slice(-2).join(".")}`];
  document.cookie
    .split(";")
    .map((c) => c.split("=")[0].trim())
    .filter((name) => name.startsWith("_ga"))
    .forEach((name) =>
      domains.forEach((d) => {
        document.cookie = `${name}=; Max-Age=0; path=/${d}`;
      })
    );
}

export function resetConsent() {
  try {
    localStorage.removeItem(STORAGE_KEYS.analyticsConsent);
  } catch {}
  window._stopAnalytics?.();
  clearAnalyticsCookies();
  window.dispatchEvent(new Event(CONSENT_RESET_EVENT));
}

interface CookieBannerProps {
  lang: Language;
}

export function CookieBanner({ lang }: CookieBannerProps) {
  const Strings = useStrings(lang);
  const [consent, setConsent] = useState<Consent | null>(readConsent);

  useEffect(() => {
    const reopen = () => setConsent(null);
    window.addEventListener(CONSENT_RESET_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_RESET_EVENT, reopen);
  }, []);

  if (consent) return null;

  const choose = (value: Consent) => {
    try {
      localStorage.setItem(STORAGE_KEYS.analyticsConsent, JSON.stringify({ value, at: Date.now() }));
    } catch {}
    if (value === "granted") {
      window._startAnalytics?.();
    } else {
      window._stopAnalytics?.();
      clearAnalyticsCookies();
    }
    setConsent(value);
  };

  const buttonClass =
    "h-[44px] bg-stone-900 text-stone-50 border border-stone-900 font-serif text-sm font-medium cursor-pointer";

  return (
    <section
      aria-label={Strings.cookieTitle}
      className="fixed inset-x-0 bottom-0 z-50 px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-stone-200 border-t border-stone-900 shadow-[0_-6px_20px_rgba(31,24,18,0.18)]"
    >
      <h2 className="font-display font-medium text-base mb-1">
        {Strings.cookieTitle}
      </h2>
      <p className="font-serif text-[13px] leading-relaxed text-stone-700 mb-3">
        {Strings.cookieText}{" "}
        <Link to={ROUTES.TERMS} className="underline underline-offset-2">
          {Strings.terms}
        </Link>
      </p>
      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => choose("denied")} className={buttonClass}>
          {Strings.cookieDecline}
        </button>
        <button onClick={() => choose("granted")} className={buttonClass}>
          {Strings.cookieAccept}
        </button>
      </div>
    </section>
  );
}
